import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Box,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' }
];

interface LanguageSelectorProps {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
  variant?: 'default' | 'minimal';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  onLanguageChange,
  variant = 'default'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(768));
  const isTablet = useMediaQuery(theme.breakpoints.between(768, 1024));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down(480));

  const handleChange = (event: SelectChangeEvent) => {
    onLanguageChange(event.target.value);
  };

  // Removed unused variable: currentLang (was computed but never used)

  // Responsive font sizes - much smaller to prevent overlap
  const getFontSize = () => {
    if (isSmallMobile) return '0.75rem'; // 12px - reduced
    if (isMobile) return '0.8125rem'; // 13px - reduced
    if (isTablet) return '0.875rem'; // 14px - reduced
    return '0.9375rem'; // 15px - desktop - much smaller
  };

  const getIconSize = () => {
    if (isSmallMobile) return '1.25rem'; // 20px - reduced
    if (isMobile) return '1.375rem'; // 22px - reduced
    if (isTablet) return '1.5rem'; // 24px - reduced
    return '1.625rem'; // 26px - desktop - much smaller
  };

  const getMinWidth = () => {
    if (isSmallMobile) return 120; // Increased to fit full language names
    if (isMobile) return 140; // Increased to fit full language names
    if (isTablet) return 150; // Increased to fit full language names
    return 160; // Increased to fit full language names like "English"
  };

  if (variant === 'minimal') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, height: '100%', maxWidth: '100%' }}>
        <FormControl
          size="small"
          sx={{
            minWidth: getMinWidth(),
            maxWidth: '100%',
            width: 'auto',
            '& .MuiOutlinedInput-root': {
              minWidth: getMinWidth(),
              maxWidth: '100%',
              borderRadius: '8px', // Smaller radius
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              height: 'auto',
              minHeight: 'auto',
              maxHeight: '40px',
              padding: '0 !important',
              '&:hover': {
                borderColor: '#9ca3af',
              },
              '&.Mui-focused': {
                borderColor: '#2563eb',
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        >
          <Select
            value={currentLanguage}
            onChange={handleChange}
            renderValue={(value) => {
              const lang = languages.find(l => l.code === value);
              return lang ? lang.nativeName : (languages[0]?.nativeName || value);
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 400,
                },
              },
            }}
            sx={{
              fontSize: getFontSize(),
              color: '#000',
              backgroundColor: 'transparent',
              padding: isSmallMobile ? '3px 8px' : isMobile ? '4px 10px' : '5px 12px', // Much smaller padding
              height: 'auto',
              minHeight: 'auto',
              '& .MuiSelect-icon': {
                color: '#000',
                fontSize: getIconSize(),
              },
              '& .MuiSelect-select': {
                padding: '0 !important',
                paddingRight: isSmallMobile ? '24px' : '28px !important',
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'clip',
                display: 'block',
                lineHeight: '1.5',
                minWidth: '0',
              },
            }}
          >
            {languages.map((lang) => (
              <MenuItem 
                key={lang.code} 
                value={lang.code} 
                sx={{ 
                  fontSize: getFontSize(), 
                  padding: isSmallMobile ? '8px 12px' : '10px 16px', 
                  borderRadius: '8px' 
                }}
              >
                {lang.nativeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, height: '100%' }}>
      <LanguageIcon sx={{ fontSize: 28, color: '#666' }} />
      <FormControl
        size="medium"
        sx={{
          minWidth: 220,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
        }}
      >
        <Select
          value={currentLanguage}
          onChange={handleChange}
          displayEmpty
          sx={{
            fontSize: '1.125rem',
            color: '#000',
            backgroundColor: '#fff',
            padding: '8px 12px',
            '& .MuiSelect-icon': {
              color: '#000',
              fontSize: '1.5rem',
            },
          }}
        >
          {languages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: '1.125rem', padding: '12px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.125rem' }}>{lang.nativeName}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  ({lang.name})
                </Typography>
              </Box>
            </MenuItem>
          ))}
          </Select>
      </FormControl>
    </Box>
  );
};

export default LanguageSelector;

